// 箱单发票列表(InvoiceRecordList.vue) 의 재료 — 순수 함수 모듈(브라우저·node 공용).
//   · 기록에 넣을 출고자료 행·박스 목록(출고리스트 엑셀에서)
//   · 出库资料文件(출고자료 엑셀) — 창고가 쓰는 샘플(GSPC26064E803A9_출고자료.xlsx, 2026-10-04 YZY) 13열 그대로
//   · 箱单发票 H 열 「CO1--1」 = 产地证 몇 번째 장 -- 그 장의 몇 번째 행
//   · 提单BL / 产地证 核对件 판독값(AI)과 우리가 만든 서류의 대조
import { COL, bundleLinesInGroups } from './warehouseListParser'
import { declaredUnitPriceUSD, confirmedNameEn } from './invoicePack'
import { Styles, Sheet, packXlsx, extraCols, fitBox } from './invoiceXlsx'
import { cooKey, zipFilesUtf8 } from './cooXlsx'
import { matchPalletMarks, matchLooseMarks, matchDescription } from './blCompareMatch'

function text(v) {
  if (v === null || v === undefined) return ''
  return String(v).replace(/\r/g, '').trim()
}
function num(v) {
  if (typeof v === 'number' && isFinite(v)) return v
  const n = parseFloat(text(v).replace(/[$,\s]/g, ''))
  return isFinite(n) ? n : 0
}
const round = (v, d) => Math.round(v * Math.pow(10, d)) / Math.pow(10, d)

// ───────────────────────── 출고자료 ─────────────────────────

// 샘플 1행 그대로(출고리스트 열 중 13개)
export const OUT_HEADER = ['주문날짜', '快递物流单号', '상품URL', '이미지URL', '주문수량', '출고수량', '박스번호', '파렛트번호', '비고', '주문번호', '영어품명', 'HS코드', '가격USD']
const OUT_WIDTH = [16.4, 10, 5.6, 4.9, 8.9, 7.6, 33.75, 13.6, 8, 8, 8, 8, 8]
const NUM_COLS = [4, 5, 12] // 주문수량·출고수량·가격USD 는 숫자칸(샘플)

// 엑셀 날짜(일련번호)로 온 주문날짜 → 「2026-09-22」
function ymdCell(v) {
  if (typeof v === 'number' && v > 30000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400000))
    return d.toISOString().slice(0, 10)
  }
  return text(v)
}

/**
 * 고른 托盘号의 출고리스트 행 → 출고자료 13열(파일 순서 그대로). 读不了的行(issues)은 빠진다.
 * 합포장 구성품(출고수량 「/」 인 부자재 줄, 대표행에 짝지어진 것만)도 뺀다 — 따로 나가지 않는다(2026-10-09 YZY).
 * @param {Array<Array>} aoa  sheet_to_json(header:1)
 * @param {object} parsed     parseWarehouseList(aoa)
 * @param {string[]} palletIds
 * @param {Object|Map} [fixedMap] 고정 申报金额(「화주|바코드」→USD, 2026-10-11 YZY) — 있으면 가격USD 를 箱单发票 申报单价 와 같게(고정금액 우선, ×N 안 함). 없으면 파일 값 그대로
 */
export function buildOutRows(aoa, parsed, palletIds, fixedMap, namesMap) {
  const c = extraCols((aoa && aoa[0]) || [])
  const src = [COL.주문날짜, COL.송장번호, COL.상품URL, COL.이미지URL, COL.주문수량, COL.수량, COL.박스번호, COL.파렛트번호, COL.실측규격, COL.주문번호, c.en, c.hs, c.price]
  const 구성품 = bundleLinesInGroups(parsed)
  return ((parsed && parsed.rows) || [])
    .filter(r => palletIds.includes(r.파렛트번호) && !구성품.has(r.line))
    .sort((a, b) => a.line - b.line)
    .map(r => {
      const raw = aoa[r.line - 1] || []
      return src.map((ci, i) => {
        const v = raw[ci]
        if (i === 0) return ymdCell(v)
        if (i === 12 && fixedMap) {
          const d = declaredUnitPriceUSD({ companyid: r.companyid, barcode: r.바코드, base: null }, fixedMap)
          if (d.fixed) return d.price
        }
        if (i === 10 && namesMap) return confirmedNameEn({ companyid: r.companyid, barcode: r.바코드, base: text(v) }, namesMap)
        if (NUM_COLS.includes(i)) return typeof v === 'number' ? v : (text(v) && isFinite(Number(text(v))) ? Number(text(v)) : text(v))
        return text(v)
      })
    })
}

// BL 확인 때 선적(출고리스트 AI 열 = BL번호)을 찾을 열쇠 — 업체·바코드·박스번호·수량(선적 자연키와 같은 재료)
export function buildBoxes(parsed, palletIds) {
  return ((parsed && parsed.groups) || [])
    .filter(g => palletIds.includes(g.파렛트번호))
    .map(g => ({ companyid: g.companyid, 바코드: g.바코드, 박스번호: g.박스번호, 수량: g.수량, 파렛트번호: g.파렛트번호 }))
}

export function writeOutboundDocXlsx(header, rows) {
  const styles = new Styles()
  const sh = new Sheet('Sheet1')
  const cols = 'ABCDEFGHIJKLM'.split('')
  cols.forEach((c, i) => sh.width(c, OUT_WIDTH[i]))
  const TH = { font: { sz: 10, b: true }, h: 'center', v: 'center', wrap: true, border: 'LRTB' }
  const TD = { font: { sz: 10 }, h: 'center', v: 'center', wrap: true, border: 'LRTB' }
  sh.height(1, 20)
  ;(header && header.length ? header : OUT_HEADER).forEach((h, i) => sh.put(cols[i] + '1', h, TH))
  rows.forEach((row, ri) => {
    const r = ri + 2
    sh.height(r, 44)
    cols.forEach((c, i) => {
      const v = row[i]
      sh.put(c + r, v === '' ? undefined : v, i === 1 || i === 9 ? Object.assign({ fmt: '@' }, TD) : TD)
    })
  })
  return packXlsx([sh], styles, 0)
}

// 파일명 — 샘플처럼 「BL번호_출고자료.xlsx」. BL 전이면 托盘号로
export function outboundDocFileName(record) {
  const clean = s => text(s).replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim()
  const head = (record.bl && record.bl.no) || (record.palletIds || []).join('、')
  return clean(head + '_출고자료') + '.xlsx'
}

// ───────────────────────── 화주별 출고리스트 ─────────────────────────
// 올린 출고리스트를 L열(업체) 화주마다 한 파일로 나눈다(2026-10-06 YZY). YZY 가 손으로 만들던 파일과 같은 꼴:
//   综合清关 「Ablecommerce_출고리스트 20260930.xlsx」 / 单独清关 「deareve_(단독통관건)출고리스트 20260930.xlsx」
//   시트 하나, 1행 = 원본 헤더, 열 A~W(주문날짜~가격USD — 오른쪽 창고 내부 열은 뺀다), 행 = 그 화주 행(원본 순서), 열 너비·행 높이 = 예시 파일.
// 상품이미지(E열)는 WPS 셀그림이라 그대로 옮기지 못한다(수식만 남으면 #NAME). 대신 이미지URL(D열)로 그림을 받아 E열에 넣는다(2026-10-18 吴亚欣 #64).
// 못 받은 칸은 빈칸 — D열 URL 은 그대로 있다.
// 화주가 여러 파일에 걸치면(综合 — 택배건+쿠팡건 등) 한 파일로 합치고, 헤더는 그 화주가 처음 나온 파일 것
const OWNER_LIST = {
  integrated: { suffix: '_출고리스트 ', rowHt: 37.5, widths: [11.75, 14.13, 8.13, 8.13, 10, 8.13, 8.13, 8.13, 8.13, 9, 8.75, 7.75, 7.75, 7.75, 7.75, 7.75, 7.75, 14.25, 9.25, 9.25, 9.25, 9.25, 9.25] },
  separate: { suffix: '_(단독통관건)출고리스트 ', rowHt: 44, widths: [16.41, 10, 5.63, 4.88, 9.88, 8.13, 8.13, 8.13, 8.13, 8.89, 7.64, 7.75, 7.75, 7.75, 7.75, 7.75, 7.75, 13.63, 8, 8, 8, 8, 8] }
}
const OWNER_COLS = 'ABCDEFGHIJKLMNOPQRSTUVW'.split('')
// 「파버네스트\nFabernest」 → 마지막 줄(warehouseListParser 와 같은 규칙)
const ownerOfCell = v => text(v).split('\n').map(s => s.trim()).filter(Boolean).pop() || ''

/** parts = [{ name, aoa }] (올린 파일들) → [{ co, header, rows }] 화주 이름순 */
// 파일 복사본(「… - 副本.xlsx」「… - 복사본.xlsx」「… (1).xlsx」)은 같은 목록이 두 번 저장된 것이다 — 화주별로 나누면 수량이 2배가 된다(2026-10-18 吴亚欣 #66).
// 복사본 표시를 뗀 이름이 같은 파일끼리는 가장 늦게 올린 것 하나만 쓴다. files = [{ fileName, uploadedAt, ... }]
export function dropCopyDuplicates(files) {
  const base = n => String(n || '').replace(/\.xlsx?$/i, '').replace(/\s*[-–]?\s*(副本|复制|복사본|copy)\s*\d*$/i, '').replace(/\s*\(\d+\)$/, '').trim()
  const best = new Map()
  ;(files || []).forEach(f => {
    const k = base(f.fileName)
    const cur = best.get(k)
    if (!cur || String(f.uploadedAt || '') > String(cur.uploadedAt || '')) best.set(k, f)
  })
  return (files || []).filter(f => best.get(base(f.fileName)) === f)
}
export function splitListsByOwner(parts) {
  const map = new Map()
  ;(parts || []).forEach(pt => {
    const header = (pt.aoa && pt.aoa[0]) || []
    ;(pt.aoa || []).slice(1).forEach(r => {
      const co = ownerOfCell(r && r[COL.업체])
      if (!co) return
      if (!map.has(co)) map.set(co, { co, header, rows: [] })
      map.get(co).rows.push(r)
    })
  })
  return [...map.values()].sort((a, b) => a.co.localeCompare(b.co))
}
// ymd = 「20260930」
// 올린 출고리스트 파일명 앞부분 → 건 종류(2026-10-16 吴亚欣 #30). 「쿠팡팔레트건」은 「쿠팡건」을 포함하지 않으니 순서 무관
export function ownerListType(fileName) {
  const n = String(fileName || '')
  if (/쿠팡팔레트건/.test(n)) return 'coupangPallet'
  if (/단독통관건/.test(n)) return 'separate'
  if (/화주건/.test(n)) return 'owner'
  if (/쿠팡건/.test(n)) return 'coupang'
  if (/택배건/.test(n)) return 'parcel'
  return ''
}
// 파일 이름 — 화주명은 늘 앞에 둔다. 종류를 모르면(옛 이름 규칙) 칸(kind)으로: 单独清关 = (단독통관건), 综合 = 접두 없음
//   화주건   → 「화주_(화주건)출고리스트 20261016」   쿠팡팔레트건 → 「화주_(쿠팡팔레트건)출고리스트 20261016」
//   단독통관건 → 「화주_(단독통관건)출고리스트 20261016」   쿠팡건 → 「그로스_화주_20261016」   택배건 → 「화주_출고리스트 20261016」
export function ownerListFileName(co, kind, ymd, type) {
  let base
  if (type === 'coupang') base = '그로스_' + co + '_' + ymd
  else if (type === 'owner') base = co + '_(화주건)출고리스트 ' + ymd
  else if (type === 'coupangPallet') base = co + '_(쿠팡팔레트건)출고리스트 ' + ymd
  else if (type === 'separate') base = co + '_(단독통관건)출고리스트 ' + ymd
  else if (type === 'parcel') base = co + '_출고리스트 ' + ymd
  else base = co + OWNER_LIST[kind].suffix + ymd
  return base.replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim() + '.xlsx'
}
const IMG_COL = 3 // D열 이미지URL
const IMG_MAX = 8
async function loadOwnerImage(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error('HTTP ' + res.status)
  const blob = await res.blob()
  const type = blob.type || ''
  if (!/image\/(png|jpe?g)/i.test(type)) throw new Error('지원 안 하는 형식 ' + type)
  const bmp = await createImageBitmap(blob)
  const img = { data: new Uint8Array(await blob.arrayBuffer()), type, width: bmp.width, height: bmp.height }
  if (bmp.close) bmp.close()
  return img
}
/** 올린 출고리스트들의 D열 이미지URL → Map(url → {data,type,width,height}). 못 받은 것은 빠진다 */
export async function fetchOwnerImages(parts) {
  const urls = new Set()
  ;(parts || []).forEach(pt => (pt.aoa || []).slice(1).forEach(r => {
    const u = text(r && r[IMG_COL]).trim()
    if (/^https?:\/\//i.test(u)) urls.add(u)
  }))
  const list = [...urls]
  const map = new Map()
  let next = 0
  const worker = async () => {
    while (next < list.length) {
      const u = list[next++]
      try { map.set(u, await loadOwnerImage(u)) } catch (e) { console.error('출고리스트 이미지 받기 실패:', u, e) }
    }
  }
  await Promise.all(Array.from({ length: Math.min(IMG_MAX, list.length) }, worker))
  return map
}
export function writeOwnerListXlsx(group, kind, imgMap) {
  const cfg = OWNER_LIST[kind]
  const styles = new Styles()
  const sh = new Sheet('Sheet1')
  const TH = { font: { sz: 9, b: true }, h: 'center', v: 'center', wrap: true, fill: 'FFF8CBAD' } // 예시 헤더 = 연주황 바탕 굵게 9pt
  const TD = { font: { sz: 9 }, h: 'center', v: 'center', wrap: true }
  OWNER_COLS.forEach((c, i) => sh.width(c, cfg.widths[i]))
  sh.height(1, 20)
  OWNER_COLS.forEach((c, i) => sh.put(c + '1', text(group.header[i]) || undefined, TH))
  group.rows.forEach((row, ri) => {
    const r = ri + 2
    sh.height(r, cfg.rowHt)
    OWNER_COLS.forEach((c, i) => {
      let v = row[i]
      if (i === COL.주문날짜) v = ymdCell(v) // 날짜 일련번호로 온 것 → 「2026-09-22」
      if (typeof v === 'string' && /^=?\s*(_xlfn\.)?DISPIMG\(/i.test(v)) v = undefined
      if (typeof v === 'number') sh.put(c + r, v, Number.isInteger(v) && Math.abs(v) >= 1e9 ? Object.assign({ fmt: '0' }, TD) : TD) // 송장번호·주문번호 지수표기 막기
      else sh.put(c + r, v === null || v === undefined ? undefined : String(v).replace(/\r/g, ''), TD)
    })
    const img = imgMap && imgMap.get(text(row[IMG_COL]).trim())
    if (img) {
      const box = { col: IMG_COL + 1, colOff: 19050, row: r - 1, rowOff: 19050, cx: Math.round((cfg.widths[IMG_COL + 1] * 7 + 5) * 9525) - 38100, cy: Math.round(cfg.rowHt * 12700) - 38100 }
      sh.images.push({ img, anchor: fitBox(img, box) })
    }
  })
  return packXlsx([sh], styles, 0)
}

// 통관유형(파렛트라벨·구매요청 요청내역 customsType) → 출고리스트 칸(설계 §4 매핑). kupass = COUPASS 명의 단독통관이라 단독 칸
export const CUSTOMS_SLOT = { integrated: 'integrated', client: 'separate', kupass: 'separate' }
// 파일명 속 날짜 — 「2026.09.30」「2026-9-30」「2026年9月30日」「20260930」 → 「2026-09-30」. 없으면 ''
export function dateFromName(name) {
  const s = String(name || '')
  const m = s.match(/(20\d{2})[.\-_/年](\d{1,2})[.\-_/月](\d{1,2})/) || s.match(/(?:^|\D)(20\d{2})(\d{2})(\d{2})(?!\d)/)
  if (!m) return ''
  const mo = Number(m[2])
  const d = Number(m[3])
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return ''
  return m[1] + '-' + String(mo).padStart(2, '0') + '-' + String(d).padStart(2, '0')
}
// 저장된 출고리스트(CoupassOutboundLists, { fileName, rows }) 의 칸 — 대표행 통관유형이 많은 쪽, 데이터가 없으면 파일명(「단독통관」), 그것도 없으면 综合
export function outboundListSlot(f) {
  let sep = 0
  let int = 0
  ;((f && f.rows) || []).forEach(r => {
    if (!r.대표여부 || !r.customsType) return
    if (CUSTOMS_SLOT[r.customsType] === 'separate') sep++
    else if (CUSTOMS_SLOT[r.customsType] === 'integrated') int++
  })
  if (sep || int) return sep > int ? 'separate' : 'integrated'
  return /단독통관|单独/.test(String((f && f.fileName) || '')) ? 'separate' : 'integrated'
}
/**
 * 「按货主拆分出库清单」 zip — 箱单发票列表 날짜 박스에서 그날 저장된 출고리스트로 내려받는다(2026-10-07 YZY 「挪到 箱单发票列表」).
 * slots = { integrated: [{ name, aoa }], separate: [...] } (칸마다 파일 순서 그대로), date = 「2026-09-30」(파일명에 날짜가 없을 때)
 * 칸 하나면 예전 이름 「出库清单_综合清关_按货主_20260930（N个）.zip」, 둘 다면 한 zip 「出库清单_按货主_20260930（N个）.zip」
 */
export function ownerListsZip(slots, date, imgMap) {
  const files = []
  const kinds = ['integrated', 'separate'].filter(k => slots[k] && slots[k].length)
  let ymd = ''
  kinds.forEach(kind => {
    const parts = slots[kind]
    const y = (dateFromName(parts[0].name) || date || '').replace(/-/g, '')
    if (!ymd) ymd = y
    // 건 종류(택배건·쿠팡건 …)가 다른 파일은 한 화주여도 따로 나눈다(2026-10-16 吴亚欣 #30). 종류를 모르는 파일은 칸 기본 이름
    const byType = new Map()
    parts.forEach(pt => { const t = ownerListType(pt.name); if (!byType.has(t)) byType.set(t, []); byType.get(t).push(pt) })
    byType.forEach((list, type) => {
      // 폴더 = 올린 출고리스트 이름(확장자 뗀 것) — 그 원본에 있던 화주 파일만 그 안에 들어간다(2026-10-16 吴亚欣 #30 연장)
      const dir = String((list[0] && list[0].name) || '').replace(/\.xlsx?$/i, '').trim()
      splitListsByOwner(list).forEach(g => files.push({ name: ownerListFileName(g.co, kind, y, type), dir, data: writeOwnerListXlsx(g, kind, imgMap) }))
    })
  })
  if (!files.length) return null
  const label = kinds.length === 1 ? (kinds[0] === 'integrated' ? '综合清关_' : '单独清关_') : ''
  return { name: '出库清单_' + label + '按货主_' + ymd + '（' + files.length + '个）.zip', data: zipFilesUtf8(files), count: files.length }
}

// ───────────────────────── 箱单发票 H 열 ─────────────────────────

/**
 * 产地证 파일들(splitCooFiles 순서) → 「영어품명|HS6」 → 「CO<몇 번째 장>--<그 장의 행>」.
 * 첫 번째 产地证 첫 행 = CO1--1, 두 번째 产地证 첫 행 = CO2--1 (2026-10-04 YZY)
 */
export function cooRefMap(files) {
  const map = new Map()
  ;(files || []).forEach((f, fi) => {
    (f.items || []).forEach((it, ii) => {
      const k = cooKey(it.desc, it.hs)
      if (!map.has(k)) map.set(k, 'CO' + (fi + 1) + '--' + (ii + 1))
    })
  })
  return map
}
// 인보이스 품목(it.desc, it.hs 10자리) → CO 번호. 产地证 이 없으면 null(H 열 빈칸)
export function coRefOf(files) {
  if (!files || !files.length) return null
  const map = cooRefMap(files)
  return it => map.get(cooKey(it.desc, it.hs)) || ''
}

// ───────────────────────── 대조 ─────────────────────────
// 결과 한 줄 = { key, label, expect, actual, status } — status: ok(일치) / warn(확인 필요) / diff(불일치) / info(대조 안 함)

function key(s) {
  return text(s).toUpperCase().replace(/[^A-Z0-9]/g, '')
}
// 같은 항구의 다른 표기(군산 = GUNSAN·KUNSAN)와 나라 이름을 지운다
const PORT_ALIAS = { KUNSAN: 'GUNSAN' }
function portKey(s) {
  let k = text(s).toUpperCase().replace(/\b(CHINA|KOREA|REPUBLIC OF|P\.?R\.?C\.?|PORT)\b/g, ' ')
  k = key(k)
  Object.keys(PORT_ALIAS).forEach(a => { k = k.replace(a, PORT_ALIAS[a]) })
  return k
}
// 회사 이름 비교 — 「CO., LTD」 같은 꼬리 말고 이름 낱말이 다 들어 있으면 같다고 본다
const NAME_STOP = ['CO', 'LTD', 'LIMITED', 'INC', 'CORP', 'COMPANY', 'THE', 'AND']
export function nameMatch(expect, actual) {
  const a = key(actual)
  if (!key(expect) || !a) return false
  if (a.includes(key(expect))) return true
  const words = text(expect).toUpperCase().split(/[^A-Z0-9]+/).filter(w => w.length >= 2 && !NAME_STOP.includes(w))
  return words.length > 0 && words.every(w => a.includes(w))
}
function firstLine(s) {
  return text(s).split('\n')[0]
}
// 무게·부피는 箱单发票 표기 자리수(소수 둘째 자리, PA 서식 0.00)로 반올림해 정확히 같아야 일치(2026-10-04 YZY — 허용 오차 없음)
function numStatus(expect, actual, ceil1) {
  if (actual === null || actual === undefined || actual === '' || !isFinite(Number(actual))) return 'diff'
  if (round(Number(actual), 2) === round(Number(expect), 2)) return 'ok'
  // 부피(CBM)는 BL 에 0.1 단위로 올려 적힌다(0.06→0.1, 0.28→0.3, 2026-10-19 吴亚欣)
  return ceil1 && round(Math.ceil(round(Number(expect), 2) * 10 - 1e-9) / 10, 1) === round(Number(actual), 2) ? 'ok' : 'diff'
}

// 기록에서 합계 — 포장 수(托+箱)·毛重·体积
export function recordTotals(record) {
  const ps = record.pallets || []
  return {
    packages: ps.reduce((s, p) => s + (num(p.count) || 0), 0),
    gw: round(ps.reduce((s, p) => s + num(p.gw), 0), 2),
    cbm: round(ps.reduce((s, p) => s + num(p.cbm), 0), 2)
  }
}

/**
 * BL 판독값 ↔ 箱单发票(기록). x = /api/coupassInvoiceRecord/readDoc(kind bl) 결과
 */
export function compareBl(record, x) {
  const out = []
  const add = (k, label, expect, actual, status) => out.push({ key: k, label, expect: text(expect), actual: text(actual), status })
  const info = record.info || {}
  const im = info.importer || {}
  const ex = info.exporter || {}
  const t = recordTotals(record)
  const vessel = text((record.coo && record.coo.info && record.coo.info.vessel) || info.carrier)

  add('blNo', '提单号(BL번호)', '', x.blNo, /^[A-Z0-9-]{6,}$/i.test(text(x.blNo).replace(/\s/g, '')) ? 'info' : 'diff')
  add('consignee', '收货人 Consignee(수하인)', im.name, firstLine(x.consignee), nameMatch(im.name, x.consignee) ? 'ok' : 'diff')
  add('shipper', '发货人 Shipper(송하인)', ex.name, firstLine(x.shipper), nameMatch(ex.name, x.shipper) ? 'ok' : 'warn')
  add('packages', '件数 Packages(포장 수)', t.packages, [x.packages, x.packageUnit].filter(v => v !== null && v !== undefined).join(' '),
    Number(x.packages) === t.packages ? 'ok' : 'diff')
  add('gw', '毛重 G/W kg(총중량)', t.gw, x.grossWeightKg, numStatus(t.gw, x.grossWeightKg))
  add('cbm', '体积 CBM(부피)', t.cbm, x.measurementCbm, numStatus(t.cbm, x.measurementCbm, true))

  // 품명 — 인보이스 품명이 BL 품명란에 다 있으면 일치. BL 은 대표 품명만 적기도 해서 빠지면 「확인 필요」
  const descs = [...new Set((record.pallets || []).reduce((a, p) => a.concat((p.items || []).map(it => text(it.desc))), []).filter(Boolean))]
  const dm = matchDescription(descs, x.description)
  add('desc', '品名 Description(품명)', descs.join(' / '), x.description + (dm.note ? '\n✓ ' + dm.note : ''), dm.status)

  // 唛头 — 托盘号가 BL Marks 에 있어야 한다(「A ~ B」 범위로 합쳐 적힌 것도 펼쳐 본다).
  // 散货 는 箱单发票 박스 번호 중 첫 번호와 끝 번호만 본다(2026-10-19 吴亚欣)
  const plts = (record.pallets || []).filter(p => !/散/.test(p.id)).map(p => p.id)
  const mm = matchPalletMarks(plts, x.marks)
  const loose = (record.pallets || []).filter(p => /散/.test(p.id)).map(p => matchLooseMarks((p.boxes || []).map(b => b.code), x.marks)).filter(l => l.first)
  const looseMissing = loose.reduce((a, l) => a.concat(l.missing), [])
  const expectMarks = plts.concat(loose.map(l => l.first + (l.last !== l.first ? ' ~ ' + l.last : '')))
  const missingAll = mm.missing.concat(looseMissing)
  const mNote = missingAll.length ? (mm.ambiguous.length ? '\n※ 범위가 모호해 펼치지 않음: ' + mm.ambiguous.join(', ') : '') + '\n※ 提单에서 못 찾은 唛头(' + missingAll.length + '): ' + missingAll.slice(0, 6).join(', ') + (missingAll.length > 6 ? ' …' : '') : (mm.viaRange ? '\n✓ 范围合并一致(범위로 합쳐 일치)' : '')
  add('marks', '唛头 Marks(托盘号)', expectMarks.join(' / ') || '-', text(x.marks) + mNote, !expectMarks.length ? 'info' : (missingAll.length ? 'warn' : 'ok'))

  const blVessel = [x.vessel, x.voyage].filter(Boolean).join(' / ')
  add('vessel', '船名航次 Vessel/Voyage(선명/항차)', vessel, blVessel,
    !vessel ? 'info' : (key(vessel).includes(key(x.vessel)) && (!x.voyage || key(vessel).includes(key(x.voyage))) ? 'ok' : 'warn'))
  add('pol', '装货港 Port of Loading(선적항)', info.portLoading, x.portOfLoading,
    !text(info.portLoading) ? 'info' : (portKey(x.portOfLoading) && portKey(info.portLoading).includes(portKey(x.portOfLoading)) ? 'ok' : 'warn'))
  add('pod', '卸货港 Port of Discharge(양하항)', info.finalDest, x.portOfDischarge,
    !text(info.finalDest) ? 'info' : (portKey(x.portOfDischarge) && portKey(info.finalDest).includes(portKey(x.portOfDischarge)) ? 'ok' : 'warn'))
  return out
}

/**
 * 产地证 核对件 판독값 ↔ 우리가 만든 产地证 한 장(record.coo.files[i]).
 * 품목은 (HS6, 품명) 으로 짝을 짓고 수량을 본다.
 */
export function compareCoo(record, file, x) {
  const out = []
  const add = (k, label, expect, actual, status) => out.push({ key: k, label, expect: text(expect), actual: text(actual), status })
  const info = record.info || {}
  const im = info.importer || {}
  const coo = (record.coo && record.coo.info) || {}
  const exporter = text(coo.exporter) || text((info.exporter || {}).name)
  const vessel = text(coo.vessel) || text(info.carrier)

  add('invoiceNo', '发票号 Invoice No.(송장번호)', file.invoiceNo, x.invoiceNo, key(file.invoiceNo) && key(file.invoiceNo) === key(x.invoiceNo) ? 'ok' : 'warn')
  add('consignee', '收货人 Consignee(수입자)', im.name, firstLine(x.consignee), nameMatch(im.name, x.consignee) ? 'ok' : 'diff')
  add('exporter', '出口商 Exporter(수출자)', firstLine(exporter), firstLine(x.exporter), nameMatch(firstLine(exporter), x.exporter) ? 'ok' : 'warn')
  add('vessel', '船名航次 Vessel(선명/항차)', vessel, x.vessel, !vessel ? 'info' : (key(x.vessel) && key(vessel) === key(x.vessel) ? 'ok' : 'warn'))
  add('date', '出运日期 Departure(출항일)', info.date, x.departureDate, !text(info.date) ? 'info' : (text(info.date) === text(x.departureDate) ? 'ok' : 'warn'))
  add('pol', '装货港 Port of Loading(선적항)', info.portLoading, x.portOfLoading,
    !text(info.portLoading) ? 'info' : (portKey(x.portOfLoading) && portKey(info.portLoading) === portKey(x.portOfLoading) ? 'ok' : 'warn'))
  add('pod', '卸货港 Port of Discharge(양하항)', info.finalDest, x.portOfDischarge,
    !text(info.finalDest) ? 'info' : (portKey(x.portOfDischarge) && portKey(info.finalDest) === portKey(x.portOfDischarge) ? 'ok' : 'warn'))

  const got = (Array.isArray(x.items) ? x.items : []).map(it => ({
    no: it.itemNo, desc: text(it.description), hs: text(it.hsCode).replace(/\D/g, '').slice(0, 6), qty: num(it.quantity), used: false
  }))
  const items = file.items || []
  add('count', '商品数 Items(품목 수)', items.length, got.length, items.length === got.length ? 'ok' : 'diff')
  items.forEach((it, i) => {
    const want = key(it.desc)
    const m = got.find(g => !g.used && g.hs === it.hs && key(g.desc) === want) ||
      got.find(g => !g.used && key(g.desc) === want) ||
      got.find(g => !g.used && g.hs === it.hs && (key(g.desc).includes(want) || want.includes(key(g.desc))))
    const label = (i + 1) + '. ' + it.desc
    const expect = it.hs + ' · ' + it.qty
    if (!m) { add('item' + (i + 1), label, expect, '', 'diff'); return }
    m.used = true
    const ok = m.hs === it.hs && m.qty === it.qty && key(m.desc) === want
    add('item' + (i + 1), label, expect, m.desc + ' · ' + m.hs + ' · ' + m.qty, ok ? 'ok' : 'diff')
  })
  got.filter(g => !g.used).forEach(g => add('extra' + g.no, '多出的品目(없어야 할 품목) ' + (g.no || ''), '', g.desc + ' · ' + g.hs + ' · ' + g.qty, 'diff'))
  return out
}

// 대조 결과 요약 — 불일치·확인 필요 개수
export function compareSummary(list) {
  const n = s => (list || []).filter(r => r.status === s).length
  return { ok: n('ok'), warn: n('warn'), diff: n('diff') }
}

export default { OUT_HEADER, buildOutRows, buildBoxes, writeOutboundDocXlsx, outboundDocFileName, splitListsByOwner, ownerListFileName, ownerListType, writeOwnerListXlsx, CUSTOMS_SLOT, dateFromName, outboundListSlot, ownerListsZip, cooRefMap, coRefOf, recordTotals, compareBl, compareCoo, compareSummary }
